import React from "react";
import { Fuel, ChevronDown, RefreshCw, MapPin, Car, Ship, CircleDollarSign, ToggleLeft, ToggleRight, ExternalLink, AlertTriangle, Truck, Flag } from "lucide-react";
import { FUEL_TYPES, formatCurrency, RUC_WEIGHT_BANDS } from "./constants";

export default function FuelHud({
  selectedFuelType, setSelectedFuelType,
  showFuelDropdown, setShowFuelDropdown,
  fuelPrices, loadingPrice, currentFuelPrice,
  fetchFuelPrices, priceSource,
  priceOverride, setPriceOverride,
  showPriceOverride, setShowPriceOverride,
  gaspyFuelPrice,
  showRegionDropdown, setShowRegionDropdown,
  regionalPrices, selectedRegion, setSelectedRegion,
  liveResult, showResultAnimation,
  rucEnabled, setRucEnabled,
  rucTier, setRucTier,
  detectedTolls, tollOverride, setTollOverride,
  ferryDetected, ferryCost, setFerryCost,
  returnTrip,
  selectedVehicle,
}) {
  const hasExtraCosts = (liveResult?.ruc_cost > 0) || (liveResult?.toll_cost > 0) || (liveResult?.ferry_cost > 0);

  return (
    <div className="sf-fuel-hud-standalone">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <label className="sf-label"><Fuel className="w-4 h-4" />FUEL TYPE</label>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setShowFuelDropdown(!showFuelDropdown)}
                className="sf-fuel-btn"
                style={{ borderColor: `${FUEL_TYPES[selectedFuelType].color}40`, color: FUEL_TYPES[selectedFuelType].color }}
                data-testid="fuel-type-selector">
                <Fuel className="w-4 h-4" />
                {loadingPrice ? <span className="sf-skeleton w-24 h-5" /> : (
                  <span data-testid="gaspy-current-price" className="font-mono">
                    {FUEL_TYPES[selectedFuelType].label}: {formatCurrency(currentFuelPrice)}/L
                    {priceOverride && parseFloat(priceOverride) > 0 ? ' *' : ''}
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showFuelDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showFuelDropdown && fuelPrices && (
                <div className="sf-dropdown" data-testid="fuel-type-dropdown">
                  {Object.entries(FUEL_TYPES).map(([key, { label, color }]) => (
                    <button key={key} onClick={() => { setSelectedFuelType(key); setShowFuelDropdown(false); setPriceOverride(""); setShowPriceOverride(false); }}
                      className={`sf-dropdown-item ${selectedFuelType === key ? 'active' : ''}`}
                      style={{ color: selectedFuelType === key ? color : '#1A1A1A' }}
                      data-testid={`fuel-option-${key}`}>
                      <span className="flex items-center gap-3"><span className="sf-dot" style={{ backgroundColor: color, boxShadow: `0 2px 8px ${color}50` }} />{label}</span>
                      <span className="text-sm font-mono font-bold">{formatCurrency(fuelPrices[key])}/L</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={fetchFuelPrices} className="sf-icon-btn" title="Refresh prices" data-testid="refresh-price-button">
              <RefreshCw className={`w-4 h-4 ${loadingPrice ? 'animate-spin' : ''}`} />
            </button>
            {priceSource && <span className="text-xs text-[#666666] bg-[rgba(229, 32, 32,0.04)] border border-[rgba(229, 32, 32,0.1)] px-3 py-1 rounded-sm font-mono">via {priceSource}</span>}
          </div>
          {/* Price Override */}
          <div className="mt-2">
            {showPriceOverride ? (
              <div className="sf-override-row" data-testid="price-override-row">
                <span className="sf-override-label">CUSTOM:</span>
                <div className="sf-override-input-wrap">
                  <span className="sf-override-dollar">$</span>
                  <input type="number" value={priceOverride} onChange={(e) => setPriceOverride(e.target.value)}
                    placeholder={gaspyFuelPrice ? gaspyFuelPrice.toFixed(2) : "0.00"}
                    className="sf-override-input" step="0.01" min="0" data-testid="price-override-input" autoFocus />
                  <span className="sf-override-unit">/L</span>
                </div>
                <button onClick={() => { setPriceOverride(""); setShowPriceOverride(false); }} className="sf-override-reset" data-testid="price-override-reset">USE GASPY</button>
              </div>
            ) : (
              <button onClick={() => setShowPriceOverride(true)} className="sf-override-toggle" data-testid="price-override-toggle">
                Enter custom price
              </button>
            )}
          </div>
          {/* Region Selector */}
          <div className="mt-3">
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setShowRegionDropdown(!showRegionDropdown)} className="sf-region-btn" data-testid="region-selector">
                <MapPin className="w-3.5 h-3.5" />
                <span>{regionalPrices?.find(r => r.region_key === selectedRegion)?.region_name || "National Average"}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showRegionDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showRegionDropdown && regionalPrices && (
                <div className="sf-dropdown max-h-[280px] overflow-y-auto" data-testid="region-dropdown">
                  {regionalPrices.map((region) => (
                    <button key={region.region_key} onClick={() => { setSelectedRegion(region.region_key); setShowRegionDropdown(false); }}
                      className={`sf-dropdown-item ${selectedRegion === region.region_key ? 'active' : ''}`}
                      data-testid={`region-option-${region.region_key}`}>
                      <span>{region.region_name}</span>
                      <span className={`text-xs font-mono ${region.offset > 0 ? 'text-[#DC2626]' : region.offset < 0 ? 'text-[#16A34A]' : 'text-[#666666]'}`}>
                        {region.offset > 0 ? '+' : ''}{region.offset !== 0 ? `${(region.offset * 100).toFixed(0)}c` : '\u2014'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RUC toggle (diesel only) */}
          {selectedFuelType === "diesel" && (
            <div className="mt-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setRucEnabled(!rucEnabled)}
                  className={`flex items-center gap-2 text-xs font-['Manrope'] px-3 py-1.5 rounded-sm border transition-all ${rucEnabled ? 'text-[#E52020] bg-[rgba(229, 32, 32,0.06)] border-[rgba(229, 32, 32,0.2)]' : 'text-[#999999] bg-[#FAFAF7] border-[#EBE6DC]'}`}
                  data-testid="ruc-toggle"
                >
                  {rucEnabled ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                  RUC
                </button>
                {rucEnabled && (
                  <select
                    value={rucTier}
                    onChange={(e) => setRucTier(e.target.value)}
                    className="text-xs font-['Manrope'] px-2 py-1.5 rounded-sm border border-[rgba(229, 32, 32,0.2)] bg-[rgba(229, 32, 32,0.03)] text-[#E52020] outline-none cursor-pointer"
                    data-testid="ruc-tier-select"
                  >
                    {RUC_WEIGHT_BANDS.map(band => {
                      const optionText = `${band.label} — $${band.rate}/1,000km`;
                      return <option key={band.key} value={band.key}>{optionText}</option>;
                    })}
                  </select>
                )}
              </div>
            </div>
          )}

          {/* Toll override */}
          {detectedTolls.length > 0 && (
            <div className="mt-2" data-testid="toll-section">
              <div className="flex items-center gap-2 text-xs text-[#666666] font-['Manrope']">
                <CircleDollarSign className="w-3.5 h-3.5 text-[#F59E0B]" />
                <span>Tolls detected: {detectedTolls.map(t => t.name).join(', ')}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-[#666666]">Override:</span>
                <div className="flex items-center bg-[#FAFAF7] border border-[#EBE6DC] rounded-sm px-2 py-1">
                  <span className="text-xs text-[#666666]">$</span>
                  <input type="number" value={tollOverride} onChange={(e) => setTollOverride(e.target.value)}
                    placeholder={detectedTolls.reduce((s, t) => s + t.cost * (returnTrip ? 2 : 1), 0).toFixed(2)}
                    className="w-16 text-xs font-mono bg-transparent outline-none text-[#1A1A1A] pl-1" step="0.10" min="0" data-testid="toll-override-input" />
                </div>
                {tollOverride && <button onClick={() => setTollOverride("")} className="text-xs text-[#E52020] font-['Manrope']">AUTO</button>}
              </div>
            </div>
          )}

          {/* Ferry cost */}
          {ferryDetected && (
            <div className="mt-2" data-testid="ferry-section">
              <div className="flex items-center gap-2 text-xs font-['Manrope']">
                <Ship className="w-3.5 h-3.5 text-[#E52020]" />
                <span className="text-[#E52020] font-semibold">Cook Strait car ferry detected</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-[#666666]">Car + driver (per crossing):</span>
                <div className="flex items-center bg-[#FAFAF7] border border-[#EBE6DC] rounded-sm px-2 py-1">
                  <span className="text-xs text-[#666666]">$</span>
                  <input type="number" value={ferryCost} onChange={(e) => setFerryCost(e.target.value)}
                    placeholder="175" className="w-16 text-xs font-mono bg-transparent outline-none text-[#1A1A1A] pl-1" step="5" min="0" data-testid="ferry-cost-input" />
                </div>
                {returnTrip && <span className="text-xs text-[#E52020] font-mono">x2</span>}
              </div>
              <div className="flex items-center gap-3 mt-2">
                <a href="https://www.bluebridge.co.nz" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-['Manrope'] font-bold text-[#E52020] bg-[#E52020]/5 border border-[#E52020]/20 rounded-sm hover:bg-[#E52020]/10 transition-colors"
                  data-testid="bluebridge-link">
                  <ExternalLink className="w-3 h-3" />BLUEBRIDGE
                </a>
                <a href="https://www.interislander.co.nz" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-['Manrope'] font-bold text-[#E52020] bg-[#E52020]/5 border border-[#E52020]/20 rounded-sm hover:bg-[#E52020]/10 transition-colors"
                  data-testid="interislander-link">
                  <ExternalLink className="w-3 h-3" />INTERISLANDER
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Estimated Cost HUD */}
        {liveResult && (
          <div className="sf-cost-hud" data-testid="calculation-result">
            <p className="sf-label mb-1">ESTIMATED COST</p>
            <div className="relative inline-block">
              {showResultAnimation && <div className="sf-result-glow" />}
              <div className={`sf-cost-value ${showResultAnimation ? 'animate' : ''}`} data-testid="calculated-total-cost">
                {formatCurrency(liveResult.total_cost)}
              </div>
            </div>
            {selectedVehicle && (
              <div className="flex items-center justify-end gap-1.5 mt-1 text-xs text-[#666666] font-['Manrope']" data-testid="selected-vehicle-display">
                <Car className="w-3.5 h-3.5 text-[#E52020]" />
                <span>{selectedVehicle.name}</span>
                <span className="font-mono text-[#999999]">{selectedVehicle.consumption_l_per_100km} L/100km</span>
              </div>
            )}
            {/* Cost breakdown */}
            <div className="mt-3 space-y-1 text-right">
              <div className="flex justify-end items-center gap-3 text-xs text-[#666666] font-mono">
                <span className="flex items-center gap-1"><Fuel className="w-3 h-3" />Fuel</span>
                <span className="w-20 text-right">{formatCurrency(liveResult.fuel_cost)}</span>
              </div>
              {liveResult.ruc_cost > 0 && (
                <div className="flex justify-end items-center gap-3 text-xs text-[#666666] font-mono" data-testid="ruc-line-item">
                  <span className="flex items-center gap-1"><Car className="w-3 h-3" />RUC</span>
                  <span className="w-20 text-right">{formatCurrency(liveResult.ruc_cost)}</span>
                </div>
              )}
              {liveResult.toll_cost > 0 && (
                <div className="flex justify-end items-center gap-3 text-xs text-[#666666] font-mono" data-testid="toll-line-item">
                  <span className="flex items-center gap-1"><CircleDollarSign className="w-3 h-3" />Tolls</span>
                  <span className="w-20 text-right">{formatCurrency(liveResult.toll_cost)}</span>
                </div>
              )}
              {liveResult.ferry_cost > 0 && (
                <div className="flex justify-end items-center gap-3 text-xs text-[#666666] font-mono" data-testid="ferry-line-item">
                  <span className="flex items-center gap-1"><Ship className="w-3 h-3" />Ferry</span>
                  <span className="w-20 text-right">{formatCurrency(liveResult.ferry_cost)}</span>
                </div>
              )}
              {hasExtraCosts && (
                <div className="flex justify-end items-center gap-3 text-xs font-mono border-t border-[rgba(229, 32, 32,0.1)] pt-1 mt-1">
                  <span className="text-[#1A1A1A] font-bold">Total</span>
                  <span className="w-20 text-right text-[#E52020] font-bold">{formatCurrency(liveResult.total_cost)}</span>
                </div>
              )}
            </div>
            <div className="mt-2 flex justify-end gap-4 text-xs text-[#666666] font-mono">
              <span className="flex items-center gap-1"><Car className="w-3 h-3" />{liveResult.distance_km} km</span>
              <span className="flex items-center gap-1"><Fuel className="w-3 h-3" />{liveResult.total_litres} L</span>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
